import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Daily.css";
import { Context } from "../../../context/Context";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { formatter, formatter2, monthNames } from "../../../utils/helpers";
import { DailyExpand } from "./dailyExpand/DailyExpand";
import { translations } from "../../../utils/lang";
import { DAILY_FIELDS2 } from "../../../utils/Daily";
import { exportToExcel } from "../../../utils/exportExcel";
import {
  deleteDailyByIdsAPI,
  fetchDailysAPI,
} from "../../../utils/api/report";
import { DeleteModal } from "../../modals/Delete";
import {
  ArrowDown,
  ArrowUp,
  Button,
  CloudDownload,
  Input,
  SearchCheckList,
  Trash2,
} from "../../ui";
import { groupBy, pruneSelection, rowToggleProps } from "../reportUtils";
import { getReportText } from "../reportText";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import { DailyPaged } from "./DailyPaged";

const DailyUnpaged = () => {
  const { lang, storeAccess, handleTokenExpiration } = useContext(Context);
  const { can, canForAll } = useRestaurantAccess();
  const [daily, setDaily] = useState([]);
  const [searchText, setSearchText] = useState({ date: "", other: "" });
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState("");
  const text = getReportText(lang);
  // The context recreates these on every provider render; a ref keeps the
  // fetcher stable so it runs once on mount.
  const contextRef = useRef({ storeAccess, handleTokenExpiration });
  contextRef.current = { storeAccess, handleTokenExpiration };
  const [checkboxFilters, setCheckboxFilters] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const maps = useMemo(
    () => ({
      app: translations[lang].filter.apps,
    }),
    [lang],
  );

  const checkboxFields = useMemo(
    () => [DAILY_FIELDS2.app, DAILY_FIELDS2.storeName, DAILY_FIELDS2.branchName],
    [],
  );

  const normalize = (val) =>
    String(val ?? "")
      .trim()
      .toLowerCase();

  const fetchDaily = useCallback(async () => {
    const { storeAccess: access, handleTokenExpiration: expire } =
      contextRef.current;
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        const result = await fetchDailysAPI(token, access);
        // Existing behaviour: the helper can't tell an expired session from
        // other failures, and a failed load is treated as expiry.
        if (result) setDaily(result);
        else expire();
      } else {
        expire();
      }
    } catch (error) {
      console.error("Fetching failed daily:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDaily();
  }, [fetchDaily]);

  const uniqueOptionsMap = useMemo(() => {
    return checkboxFields.reduce((acc, field) => {
      const set = new Set(
        daily
          .map((item) => item[field])
          .filter(Boolean)
          .map(normalize),
      );
      acc[field] = Array.from(set);
      return acc;
    }, {});
  }, [daily, checkboxFields]);

  const getOptionLabel = (field, val) => {
    if (field === DAILY_FIELDS2.app) {
      return maps[field]?.[val] ?? translations[lang].notApp + " " + val[val.length - 1];
    }

    return maps[field]?.[val] ?? val;
  };

  const filteredBranches = useMemo(() => {
    const selectedStores = checkboxFilters[DAILY_FIELDS2.storeName] || [];

    if (selectedStores.length === 0) {
      return uniqueOptionsMap[DAILY_FIELDS2.branchName] || [];
    }

    const branches = new Set(
      daily
        .filter((item) => selectedStores.includes(normalize(item[DAILY_FIELDS2.storeName])))
        .map((item) => item[DAILY_FIELDS2.branchName])
        .filter(Boolean)
        .map(normalize),
    );

    return Array.from(branches);
  }, [checkboxFilters, daily, uniqueOptionsMap]);

  const getOptions = (field) => {
    const values = field === DAILY_FIELDS2.branchName ? filteredBranches : uniqueOptionsMap[field] || [];

    return values.map((val) => ({
      value: val,
      label: getOptionLabel(field, val),
    }));
  };

  const filteredDaily = useMemo(() => {
    return daily.filter((item) => {
      if (searchText.date) {
        const [year, monthNum] = searchText.date.split("-");
        const selectedMonthName = monthNames[monthNum];

        const itemDate = item[DAILY_FIELDS2.date] || "";
        const itemMonthRaw = itemDate.split("-")[1];
        const itemYearRaw = itemDate.split("-")[2];

        if (
          !itemMonthRaw ||
          itemMonthRaw !== selectedMonthName ||
          !itemYearRaw ||
          itemYearRaw !== year
        )
          return false;
      }

      for (const field of checkboxFields) {
        const active = checkboxFilters[field];
        if (active?.length) {
          const itemValue = normalize(item[field]);
          const normalizedActive = active.map(normalize);
          if (!normalizedActive.includes(itemValue)) return false;
        }
      }

      return true;
    });
  }, [checkboxFields, checkboxFilters, daily, searchText]);

  const summarizedData = useMemo(() => {
    const groupedSummary = {};

    filteredDaily.forEach((item) => {
      const store = item[DAILY_FIELDS2.storeName];
      const groupKey = store;

      if (!groupedSummary[groupKey]) {
        groupedSummary[groupKey] = {
          store: store,
          rejected: 0,
          rejectedAmount: 0,
          totalOrder: 0,
          canceled: 0,
          canceledAmount: 0,
          refund: 0,
          endurance: 0,
          refundAmount: 0,
          enduranceAmount: 0,
          delivered: 0,
        };
      }

      groupedSummary[groupKey].rejected += parseFloat(item[DAILY_FIELDS2.rejected] || 0) || 0;
      groupedSummary[groupKey].rejectedAmount += parseFloat(item[DAILY_FIELDS2.rejectedAmount] || 0) || 0;
      groupedSummary[groupKey].totalOrder += parseFloat(item[DAILY_FIELDS2.completedAmount] || 0);
      groupedSummary[groupKey].canceled += parseFloat(item[DAILY_FIELDS2.canceled] || 0);
      groupedSummary[groupKey].canceledAmount += parseFloat(item[DAILY_FIELDS2.canceledAmount] || 0);
      groupedSummary[groupKey].delivered += parseFloat(item[DAILY_FIELDS2.completed] || 0);
      groupedSummary[groupKey].endurance += parseFloat(item[DAILY_FIELDS2.endurance] || 0);
      groupedSummary[groupKey].refund += parseFloat(item[DAILY_FIELDS2.refund] || 0);
      groupedSummary[groupKey].enduranceAmount += parseFloat(item[DAILY_FIELDS2.enduranceAmount] || 0);
      groupedSummary[groupKey].refundAmount += parseFloat(item[DAILY_FIELDS2.refundAmount] || 0);
    });

    return Object.values(groupedSummary);
  }, [filteredDaily]);

  // Rows per store, built once (each summary row used to re-filter the whole
  // filtered dataset).
  const rowsByStore = useMemo(
    () => groupBy(filteredDaily, (row) => row[DAILY_FIELDS2.storeName]),
    [filteredDaily],
  );

  const deleteByIdsName = async () => {
    const token = localStorage.getItem("auth-token");
    const ids = filteredDaily.map((item) => item._id);

    if (!ids.length) return;
    // Handler-level authorization: the bulk-delete permission and every affected
    // restaurant must pass (rows carry the restaurant _id).
    if (!can("report.bulkDelete") || !canForAll("report.entry.delete", filteredDaily.map((item) => item.storeId))) return;
    if (token) {
      const result = await deleteDailyByIdsAPI(token, ids);
      if (result) {
        setDeleteTarget(null);
        setDeleteError("");
        fetchDaily();
      } else {
        // Keep the dialog open so the failure is visible and can be retried.
        setDeleteError(text.actionFailed);
      }
    } else {
      handleTokenExpiration();
    }
  };

  // Changing stores clears branch selections that no longer belong to any
  // selected store (they kept filtering while their tags showed no label).
  const handleCheckboxChange = (field, selected) => {
    setCheckboxFilters((prev) => {
      const next = { ...prev, [field]: selected };
      if (field !== DAILY_FIELDS2.storeName) return next;
      const selectedStores = next[DAILY_FIELDS2.storeName] || [];
      const available = new Set(
        daily
          .filter(
            (item) =>
              !selectedStores.length ||
              selectedStores.includes(normalize(item[DAILY_FIELDS2.storeName])),
          )
          .map((item) => normalize(item[DAILY_FIELDS2.branchName])),
      );
      next[DAILY_FIELDS2.branchName] = pruneSelection(prev[DAILY_FIELDS2.branchName], [...available]);
      return next;
    });
  };

  const hasActiveFilters =
    Boolean(searchText.date) ||
    Object.values(checkboxFilters).some((values) => values?.length);

  const handleClearFilters = () => {
    setSearchText({ date: "", other: "" });
    setCheckboxFilters({});
  };

  return (
    <div className="report-daily-table">
      <div className="report-daily-search-panel">
        <div className="report-daily-text-filter">
          <Input
            type="month"
            fullWidth={false}
            label={translations[lang].reports.daily.inputTitles[0]}
            value={searchText.date || ""}
            onChange={(e) =>
              setSearchText((prev) => ({ ...prev, date: e.target.value }))
            }
          />

          {checkboxFields.map((field, i) => (
            <SearchCheckList
              key={field}
              placeholder={translations[lang].reports.daily.inputTitles[i + 1]}
              options={getOptions(field)}
              selected={checkboxFilters[field] || []}
              onChange={(selected) => handleCheckboxChange(field, selected)}
            />
          ))}
        </div>

        <div className="report-filter-actions">
          <Button  variant="warning" appearance="soft" size="sm" onClick={handleClearFilters}>
            {translations[lang].reports.daily.inputTitles[4]}
          </Button>
        <div className="exportToExcel">
          <Button
            className="report-export-btn"
            variant="primary"
            appearance="soft"
            size="sm"
            icon={<CloudDownload />}
            iconPosition="end"
            aria-label={text.exportLabel(filteredDaily.length)}
            title={text.exportLabel(filteredDaily.length)}
            disabled={!filteredDaily.length}
            onClick={() => exportToExcel(filteredDaily, "Daily")}
          >
            {text.export}
          </Button>
        </div>

          {can("report.bulkDelete") && (
            <div
                         className="delete-by-container"
                         style={{ marginInlineStart: "auto" }}
                       >
                       <Button
                         variant="danger"
                           appearance=""
                           size="sm"
                         rounded
                                        icon={<Trash2 size={18} />}
              title={text.deleteFiltered}
                aria-label={text.deleteFiltered}
                disabled={!filteredDaily.length}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(true);
              }}
            />
            </div>
          )}
        </div>
      </div>

      <div className="report-daily-card-col">
        <div className="report-daily-header-card">
          {translations[lang].reports.daily.header.map((item, i) => (
            <p key={i}>
              <strong>{item}</strong>
            </p>
          ))}
        </div>
        {loading && (
          <p className="report-state" role="status">
            {text.loading}
          </p>
        )}
        {!loading && summarizedData.length === 0 && (
          <p className="report-state">{text.empty}</p>
        )}
        {summarizedData.map((summary) => {
          // Keyed by store: an index pointed at a different store once the
          // filters changed the list.
          const isExpanded = expandedGroup === summary.store;
          const groupDetails = rowsByStore.get(summary.store) || [];
          const isApproved = groupDetails.find((a) => a.isApproved === false);

          return (
            <React.Fragment key={summary.store}>
              <div
                {...rowToggleProps(isExpanded, () =>
                  setExpandedGroup(isExpanded ? null : summary.store),
                )}
                className={`report-daily-summary-card ${
                  isExpanded ? "colored" : ""
                } ${isApproved && !isApproved?.isApproved ? "notApproved" : ""}`}
              >
                <div className="report-daily-p center">
                  <p>
                    <strong>{summary.store}</strong>
                  </p>
                  {isApproved && (
                    <span className="report-pending-tag">{text.pendingApproval}</span>
                  )}
                </div>
                <div className="report-daily-p">
                  <p>
                    <strong>{formatter2.format(summary.delivered)}</strong> /{" "}
                    <strong>{formatter.format(summary.totalOrder.toFixed(2))}</strong>
                  </p>
                </div>
                <div className="report-daily-p">
                  <p>
                    <strong>{formatter2.format(summary.rejected)}</strong> /{" "}
                    <strong>{formatter.format(summary.rejectedAmount)}</strong>
                  </p>
                </div>
                <div className="report-daily-p">
                  <p>
                    <strong>{formatter2.format(summary.canceled)}</strong> /{" "}
                    <strong>{formatter.format(summary.canceledAmount)}</strong>
                  </p>
                </div>
                <div className="report-daily-p">
                  <p>
                    <strong>{formatter2.format(summary.endurance)}</strong> /{" "}
                    <strong>{formatter.format(summary.enduranceAmount)}</strong>
                  </p>
                </div>
                <div className="report-daily-p">
                  <p>
                    <strong>{formatter2.format(summary.refund)}</strong> /{" "}
                    <strong>{formatter.format(summary.refundAmount)}</strong>
                  </p>
                </div>
                <div className="report-expand-icon">
                  {isExpanded ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
                </div>
              </div>
              {isExpanded && (
                <DailyExpand data={groupDetails} onRefresh={fetchDaily} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {deleteTarget && (
        <DeleteModal
          cancel={() => {
            setDeleteTarget(null);
            setDeleteError("");
          }} id={deleteTarget} handleDelete={deleteByIdsName}
          error={deleteError}
          description={`${text.deleteFilteredDescription(filteredDaily.length)}${
            hasActiveFilters ? "" : ` ${text.deleteAllWarning}`
          }`}
        />
      )}
    </div>
  );
};

// PAGED_REPORTS on: server-side filtering, sorting and paging; off (default): the table above, unchanged.
export const Daily = ({ active = true }) =>
  isFeatureEnabled(FEATURE_NAMES.PAGED_REPORTS) ? <DailyPaged active={active} /> : <DailyUnpaged />;
